import { lazy, Suspense, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { call } from '@edil/api-client';
import {
  Button,
  EmptyState,
  IconAlertCircle,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconCheck,
  IconGrid,
  IconHistory,
  IconLock,
  IconLogOut,
  IconSettings,
  IconShieldCheck,
  IconTicket,
  IconTrophy,
  IconUser,
  IconGift,
  IconUsers,
  IconSparkle,
  IconBell,
  IconWallet,
  Skeleton,
} from '@edil/ui';
import { useApp } from '@/lib/app';
import { Link, usePathname, useRouter } from '@/router';
import { LoginScreen } from '@/screens/Login';

const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) => lazy(() => load().then((m) => ({ default: m[name] })));

/** Route → screen and the permission it needs (the API enforces the same permission). */
const ROUTES: Record<string, { screen: ComponentType; permission?: string }> = {
  '/': { screen: named(() => import('@/screens/Dashboard'), 'Dashboard') },
  '/approvals/': { screen: named(() => import('@/screens/Approvals'), 'Approvals') },
  '/players/': { screen: named(() => import('@/screens/Players'), 'PlayerSearch'), permission: 'players.read' },
  '/players/view/': { screen: named(() => import('@/screens/Players'), 'PlayerView'), permission: 'players.read' },
  '/rounds/': { screen: named(() => import('@/screens/Rounds'), 'RoundList'), permission: 'rounds.read' },
  '/rounds/view/': { screen: named(() => import('@/screens/Rounds'), 'RoundView'), permission: 'rounds.read' },
  '/rooms/': { screen: named(() => import('@/screens/Rooms'), 'Rooms'), permission: 'rounds.read' },
  '/bingo/rooms/': { screen: named(() => import('@/screens/Bingo'), 'BingoRooms'), permission: 'rounds.read' },
  '/bingo/rounds/': { screen: named(() => import('@/screens/Bingo'), 'BingoRoundList'), permission: 'rounds.read' },
  '/bingo/rounds/view/': { screen: named(() => import('@/screens/Bingo'), 'BingoRoundView'), permission: 'rounds.read' },
  '/risk/': { screen: named(() => import('@/screens/Risk'), 'RiskCases'), permission: 'players.read' },
  '/deposits/': { screen: named(() => import('@/screens/Deposits'), 'DepositQueue'), permission: 'cashier.read' },
  '/deposits/review/': { screen: named(() => import('@/screens/Deposits'), 'DepositReview'), permission: 'cashier.read' },
  '/withdrawals/': { screen: named(() => import('@/screens/Withdrawals'), 'WithdrawalQueue'), permission: 'cashier.read' },
  '/withdrawals/view/': { screen: named(() => import('@/screens/Withdrawals'), 'WithdrawalView'), permission: 'cashier.read' },
  '/accounts/': { screen: named(() => import('@/screens/Accounts'), 'Accounts'), permission: 'cashier.read' },
  '/statements/': { screen: named(() => import('@/screens/Statements'), 'Statements'), permission: 'cashier.read' },
  '/alerts/': { screen: named(() => import('@/screens/Alerts'), 'Alerts'), permission: 'cashier.read' },
  '/finance/': { screen: named(() => import('@/screens/Finance'), 'Finance'), permission: 'finance.read' },
  '/referrals/': { screen: named(() => import('@/screens/Referrals'), 'Referrals'), permission: 'referrals.read' },
  '/growth/': { screen: named(() => import('@/screens/Growth'), 'Growth'), permission: 'referrals.read' },
  '/engagement/': { screen: named(() => import('@/screens/Engagement'), 'Engagement'), permission: 'marketing.read' },
  '/promotions/': { screen: named(() => import('@/screens/Promotions'), 'Promotions'), permission: 'marketing.read' },
  '/settings/': { screen: named(() => import('@/screens/Settings'), 'Settings'), permission: 'config.read' },
  '/audit/': { screen: named(() => import('@/screens/Audit'), 'AuditLog'), permission: 'audit.read' },
  '/admins/': { screen: named(() => import('@/screens/Admins'), 'Admins'), permission: 'admins.manage' },
};

export function App() {
  const { admin, ready, can } = useApp();
  const path = usePathname();
  const router = useRouter();
  const normalized = path.endsWith('/') ? path : `${path}/`;

  useEffect(() => {
    if (ready && !admin && normalized !== '/login/') router.replace('/login/');
    if (ready && admin && normalized === '/login/') router.replace('/');
  }, [ready, admin, normalized, router]);

  if (!ready) return null;
  if (!admin) return <LoginScreen />;
  const route = ROUTES[normalized];
  let content: ReactNode;
  if (!route) content = <EmptyState icon={<IconGrid />} title="Page not found" />;
  else if (route.permission && !can(route.permission))
    content = (
      <EmptyState icon={<IconLock />} title="No access">
        Your roles do not include this area. Ask a Super Admin.
      </EmptyState>
    );
  else content = <route.screen />;
  return (
    <Shell>
      <Suspense fallback={<Skeleton height={320} radius={16} />}>{content}</Suspense>
    </Shell>
  );
}

type Counts = { deposits: number; withdrawals: number; alerts: number; approvals: number };

function Shell({ children }: { children: ReactNode }) {
  const { admin, signOut, api, can } = useApp();
  const path = usePathname();
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    const load = () =>
      call(api.GET('/admin/v1/dashboard'))
        .then((d) => setCounts({ deposits: d.cashier.deposits_waiting, withdrawals: d.cashier.withdrawals_open, alerts: d.cashier.alerts_open, approvals: d.approvals_pending }))
        .catch(() => {});
    void load();
    const id = setInterval(load, 15_000);
    // Screens announce decisions so the counts update at once.
    window.addEventListener('edil:cashier-changed', load);
    return () => {
      clearInterval(id);
      window.removeEventListener('edil:cashier-changed', load);
    };
  }, [api, path]);

  const nav = (href: string, label: string, icon: ReactNode, opts: { count?: number; permission?: string } = {}) => {
    if (opts.permission && !can(opts.permission)) return null;
    const active = href === '/' ? path === '/' : path.startsWith(href);
    return (
      <Link key={href} href={href} className={`nav${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
        {icon} {label}
        {opts.count ? <span className="navCount">{opts.count}</span> : null}
      </Link>
    );
  };
  const section = (label: string, items: ReactNode[]) =>
    items.some(Boolean) ? (
      <nav className="navSection" aria-label={label}>
        <span className="navLabel">{label}</span>
        {items}
      </nav>
    ) : null;

  return (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <span className="brandMark">
            <IconShieldCheck size={18} />
          </span>
          <span>
            Edil <span className="brandSub">Back office</span>
          </span>
        </div>
        <div className="sideScroll">
          {section('Operations', [
            nav('/', 'Overview', <IconGrid size={18} />),
            nav('/approvals/', 'Approvals', <IconCheck size={18} />, { count: counts?.approvals }),
            nav('/players/', 'Players', <IconUser size={18} />, { permission: 'players.read' }),
            nav('/risk/', 'Risk cases', <IconAlertCircle size={18} />, { permission: 'players.read' }),
            nav('/rounds/', 'Rounds', <IconTicket size={18} />, { permission: 'rounds.read' }),
            nav('/rooms/', 'Rooms', <IconTrophy size={18} />, { permission: 'rounds.read' }),
            nav('/bingo/rounds/', 'Bingo rounds', <IconTicket size={18} />, { permission: 'rounds.read' }),
            nav('/bingo/rooms/', 'Bingo rooms', <IconTrophy size={18} />, { permission: 'rounds.read' }),
          ])}
          {section('Cashier', [
            nav('/deposits/', 'Deposits', <IconArrowDownLeft size={18} />, { count: counts?.deposits, permission: 'cashier.read' }),
            nav('/withdrawals/', 'Withdrawals', <IconArrowUpRight size={18} />, { count: counts?.withdrawals, permission: 'cashier.read' }),
            nav('/accounts/', 'Receiving accounts', <IconWallet size={18} />, { permission: 'cashier.read' }),
            nav('/statements/', 'Reconciliation', <IconShieldCheck size={18} />, { permission: 'cashier.read' }),
            nav('/alerts/', 'Risk alerts', <IconAlertCircle size={18} />, { count: counts?.alerts, permission: 'cashier.read' }),
          ])}
          {section('Finance & platform', [
            nav('/finance/', 'Finance', <IconWallet size={18} />, { permission: 'finance.read' }),
            nav('/growth/', 'Growth', <IconSparkle size={18} />, { permission: 'referrals.read' }),
            nav('/engagement/', 'Engagement', <IconBell size={18} />, { permission: 'marketing.read' }),
            nav('/promotions/', 'Deposit bonuses', <IconGift size={18} />, { permission: 'marketing.read' }),
            nav('/referrals/', 'Referrals', <IconGift size={18} />, { permission: 'referrals.read' }),
            nav('/settings/', 'Settings & flags', <IconSettings size={18} />, { permission: 'config.read' }),
            nav('/audit/', 'Audit log', <IconHistory size={18} />, { permission: 'audit.read' }),
            nav('/admins/', 'Admin users', <IconUsers size={18} />, { permission: 'admins.manage' }),
          ])}
        </div>
        <div className="sideFoot">
          <span className="sideName">{admin?.display_name}</span>
          <span>{admin?.email}</span>
          <span>{admin?.roles.join(', ')}</span>
          <Button variant="secondary" size="sm" iconLeft={<IconLogOut size={16} />} onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </aside>
      <main className="main">{children}</main>
    </div>
  );
}
